import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { User, Session } from "@supabase/supabase-js";
import { supabase } from "./client";

const SITE_URL = (
  (import.meta.env as Record<string, string>)["VITE_SITE_URL"] ??
  "https://mondivago.com"
).replace(/\/$/, "");

type UserRole =
  | "supplier"
  | "traveler"
  | "partner"
  | "admin"
  | "individual"
  | "agency";

interface AuthState {
  user: User | null;
  session: Session | null;
  role: UserRole | null;
  isSupplier: boolean;
  isTraveler: boolean;
  isPartner: boolean;
  isAdmin: boolean;
  /** Only for supplier workers: the agency supplier_id */
  agencyId: string | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    redirectPath?: string,
    metadata?: Record<string, unknown>,
  ) => Promise<{ error: Error | null }>;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{
    error: Error | null;
    role: UserRole | null;
    user?: User | null;
  }>;
  signInWithGoogle: (redirectPath?: string) => Promise<{ error: Error | null }>;
  signOut: (redirect?: boolean) => Promise<void>;
  refreshSupplierStatus: () => Promise<void>;
  refreshTravelerStatus: () => Promise<void>;
  resendSignupOtp: (
    email: string,
    redirectPath?: string,
  ) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/** Normalises any metadata value to a lower‑case string for comparison. */
const meta = (val: unknown): string =>
  val == null ? "" : String(val).toLowerCase();

/** Reads the first truthy metadata key by priority. */
const readMeta = (user: User | null, ...keys: string[]): string => {
  if (!user) return "";
  for (const key of keys) {
    for (const src of [user.user_metadata, user.app_metadata]) {
      const v = (src as Record<string, unknown>)?.[key];
      if (v != null && v !== "") return meta(v);
    }
  }
  return "";
};

/** Single source of truth for a user's role — metadata only (fast, no DB). */
const deduceRole = (user: User | null): UserRole | null => {
  if (!user) return null;
  const r = readMeta(user, "role");
  if (r === "admin") return "admin";
  if (r === "supplier") return "supplier";
  if (r === "partner") return "partner";
  if (r === "traveler") return "traveler";
  return null;
};

// ── Pure helpers (no component state) ─────────────────────────────────────
interface SupplierInfo {
  isSupplier: boolean;
  role: "individual" | "agency" | "admin" | null;
  agencyId: string | null;
}

/** Verifies supplier status in DB and returns enriched info. */
const fetchSupplierInfo = async (u: User): Promise<SupplierInfo> => {
  if (deduceRole(u) === "admin") {
    return { isSupplier: false, role: "admin" as const, agencyId: null };
  }

  // 1. Check supplier_workers first (all supplier roles live here)
  const { data: workerData } = await supabase
    .from("supplier_workers")
    .select("role, supplier_id")
    .eq("user_id", u.id)
    .maybeSingle();

  if (workerData) {
    const workerRole = workerData.role;
    const { data: supData } = await supabase
      .from("suppliers")
      .select("type")
      .eq("id", workerData.supplier_id)
      .maybeSingle();

    const isAgency = supData?.type === "agency";

    if (workerRole === "owner" && isAgency) {
      return { isSupplier: true, role: "agency" as const, agencyId: null };
    }
    if (workerRole === "owner") {
      return { isSupplier: true, role: "individual" as const, agencyId: null };
    }
    return {
      isSupplier: true,
      role: "individual" as const,
      agencyId: workerData.supplier_id,
    };
  }

  // 2. No worker row yet — check if suppliers row exists (created by trigger)
  const { data: existingSupplier } = await supabase
    .from("suppliers")
    .select("id, type")
    .eq("user_id", u.id)
    .maybeSingle();

  if (existingSupplier) {
    const swName = String(u.user_metadata?.full_name ?? "User");
    const [swFn, ...swLn] = swName.split(" ");
    await supabase.rpc("create_supplier_worker", {
      p_user_id: u.id,
      p_supplier_id: existingSupplier.id,
      p_first_name: swFn || "User",
      p_last_name: swLn.join(" ") || "Mondivago",
      p_email: u.email ?? "",
    });

    if (existingSupplier.type === "agency") {
      return { isSupplier: true, role: "agency" as const, agencyId: null };
    }
    return { isSupplier: true, role: "individual" as const, agencyId: null };
  }

  // 3. No rows at all — check metadata and auto-create both
  const metaRole = deduceRole(u);
  if (metaRole !== "supplier") {
    return { isSupplier: false, role: null, agencyId: null };
  }

  const dbType =
    String(u.user_metadata?.supplier_type ?? "individual") === "agency"
      ? "agency"
      : "individual";
  const fullName = String(u.user_metadata?.full_name ?? "User");

  const { data: inserted, error: insertError } = await supabase
    .from("suppliers")
    .upsert(
      {
        user_id: u.id,
        email: u.email ?? "",
        company_name: fullName,
        status: "active",
        type: dbType,
      },
      { onConflict: "user_id" },
    )
    .select("id, type")
    .single();

  if (insertError || !inserted) {
    return { isSupplier: false, role: null, agencyId: null };
  }

  const [fn, ...ln] = fullName.split(" ");
  await supabase.rpc("create_supplier_worker", {
    p_user_id: u.id,
    p_supplier_id: inserted.id,
    p_first_name: fn || "User",
    p_last_name: ln.join(" ") || "Mondivago",
    p_email: u.email ?? "",
  });

  if (inserted.type === "agency") {
    return { isSupplier: true, role: "agency" as const, agencyId: null };
  }
  return { isSupplier: true, role: "individual" as const, agencyId: null };
};

interface PartnerInfo {
  isPartner: boolean;
}

/** Verifies partner status in DB and returns enriched info / auto-creates worker. */
const fetchPartnerInfo = async (u: User): Promise<PartnerInfo> => {
  if (deduceRole(u) === "admin") {
    return { isPartner: false };
  }

  // 1. Check partner_workers first
  const { data: workerData } = await supabase
    .from("partner_workers")
    .select("id, partner_id")
    .eq("user_id", u.id)
    .maybeSingle();

  if (workerData) {
    return { isPartner: true };
  }

  // 2. No worker row yet - check if partners row exists (created by old trigger)
  const { data: existingPartner } = await supabase
    .from("partners")
    .select("id, type")
    .eq("user_id", u.id)
    .maybeSingle();

  if (existingPartner) {
    const swName = String(u.user_metadata?.full_name ?? "Partner Agent");
    const [swFn, ...swLn] = swName.split(" ");
    await supabase.from("partner_workers").insert({
      user_id: u.id,
      partner_id: existingPartner.id,
      first_name: swFn || "Partner",
      last_name: swLn.join(" ") || "Agent",
      email: u.email ?? "",
      role: "operator",
      is_active: true,
    });

    return { isPartner: true };
  }

  // 3. No rows at all - check metadata and auto-create both
  const metaRole = deduceRole(u);
  if (metaRole !== "partner") {
    return { isPartner: false };
  }

  const dbType =
    String(u.user_metadata?.partner_type ?? "reseller") === "affiliate"
      ? "affiliate"
      : "reseller";
  const fullName = String(u.user_metadata?.full_name ?? "Partner Company");
  const refCode =
    "PART_" + Math.random().toString(36).substring(2, 10).toUpperCase();

  const { data: inserted, error: insertError } = await supabase
    .from("partners")
    .upsert(
      {
        user_id: u.id,
        company_name: "",
        type: dbType,
        ref_code: refCode,
        status: "active",
      },
      { onConflict: "user_id" },
    )
    .select("id")
    .single();

  if (insertError || !inserted) {
    return { isPartner: false };
  }

  const [fn, ...ln] = fullName.split(" ");
  await supabase.from("partner_workers").insert({
    user_id: u.id,
    partner_id: inserted.id,
    first_name: fn || "Partner",
    last_name: ln.join(" ") || "Agent",
    email: u.email ?? "",
    role: "operator",
    is_active: true,
  });

  return { isPartner: true };
};

/** Ensures a traveler row exists for users without a known role. */
const ensureTraveler = async (u: User): Promise<boolean> => {
  const { data, error } = await supabase
    .from("travelers")
    .select("id")
    .eq("user_id", u.id)
    .limit(1);
  if (!error && data?.length) return true;

  const { count: s } = await supabase
    .from("suppliers")
    .select("*", { count: "exact", head: true })
    .eq("user_id", u.id);
  const { count: sw } = await supabase
    .from("supplier_workers")
    .select("*", { count: "exact", head: true })
    .eq("user_id", u.id);
  const { count: p } = await supabase
    .from("partners")
    .select("*", { count: "exact", head: true })
    .eq("user_id", u.id);
  const { count: pw } = await supabase
    .from("partner_workers")
    .select("*", { count: "exact", head: true })
    .eq("user_id", u.id);
  if ((s ?? 0) > 0 || (sw ?? 0) > 0 || (p ?? 0) > 0 || (pw ?? 0) > 0)
    return false;

  const fullName = String(u.user_metadata?.full_name ?? "Traveler");
  const [fn, ...ln] = fullName.split(" ");
  await supabase.from("travelers").insert({
    user_id: u.id,
    first_name: fn || "Traveler",
    last_name: ln.join(" ") || "",
    email: u.email ?? "",
  });
  return true;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [agencyId, setAgencyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /** Resolves the final role and updates state. */
  const resolveRole = useCallback(async (u: User): Promise<UserRole | null> => {
    const metaRole = deduceRole(u);

    if (metaRole === "admin") {
      setRole("admin");
      setAgencyId(null);
      return "admin";
    }

    // 1. Check if user is a supplier (owner or team worker)
    const supplierInfo = await fetchSupplierInfo(u);
    if (supplierInfo.isSupplier) {
      setRole(supplierInfo.role);
      setAgencyId(supplierInfo.agencyId);
      return supplierInfo.role;
    }

    // 2. Check if user is a partner (owner or team worker)
    const partnerInfo = await fetchPartnerInfo(u);
    if (partnerInfo.isPartner) {
      setRole("partner");
      setAgencyId(null);
      return "partner";
    }

    // 3. Default to traveler
    await ensureTraveler(u);
    setRole("traveler");
    setAgencyId(null);
    return "traveler";
  }, []);

  // ── Init & listeners ─────────────────────────────────────────────
  useEffect(() => {
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        setSession(session);
        const u = session?.user ?? null;
        setUser(u);
        if (u) await resolveRole(u);
        setLoading(false);
      })
      .catch(() => {
        // Invalid/expired session — clear stale auth state
        setUser(null);
        setSession(null);
        setRole(null);
        setAgencyId(null);
        setLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const u = session?.user ?? null;

      // Skip redundant token‑refresh events (fires on every tab focus)
      if (event === "TOKEN_REFRESHED") {
        setSession((prev) =>
          prev?.access_token !== session?.access_token ? session : prev,
        );
        return;
      }

      if (event === "USER_UPDATED") {
        setSession(session);
        setUser(u);
        return;
      }

      setSession(session);
      setUser(u);
      if (u) {
        resolveRole(u)
          .then(() => setLoading(false))
          .catch(() => setLoading(false));
      } else {
        setRole(null);
        setAgencyId(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auth actions ─────────────────────────────────────────────────
  const signUp = useCallback(
    async (
      email: string,
      password: string,
      redirectPath = "/onboarding",
      metadata?: Record<string, unknown>,
    ) => {
      const emailRedirectTo = `${SITE_URL}/auth/callback?next=${encodeURIComponent(redirectPath)}`;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo, data: metadata },
      });
      if (error) return { error };
      if (data.user && (data.user.identities?.length ?? 0) === 0) {
        return {
          error: new Error("This email is already registered. Try signing in."),
        };
      }
      return { error: null };
    },
    [],
  );

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) return { error, role: null, user: null };
    const resolvedRole = await resolveRole(data.user);
    setUser(data.user);
    return { error: null, role: resolvedRole, user: data.user };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInWithGoogle = useCallback(
    async (redirectPath = "/trips/bookings") => {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${SITE_URL}/auth/callback?next=${encodeURIComponent(redirectPath)}`,
        },
      });
      return { error };
    },
    [],
  );

  const signOut = useCallback(async (redirect = true) => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole(null);
    setAgencyId(null);
    if (redirect) window.location.href = "/";
  }, []);

  const refreshSupplierStatus = useCallback(async () => {
    if (!user) return;
    const info = await fetchSupplierInfo(user);
    setRole(info.isSupplier ? info.role : null);
  }, [user]);

  const refreshTravelerStatus = useCallback(async () => {
    if (!user) return;
    await ensureTraveler(user);
    setRole("traveler");
  }, [user]);

  const resendSignupOtp = useCallback(
    async (email: string, redirectPath = "/onboarding") => {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: `${SITE_URL}/auth/callback?next=${encodeURIComponent(redirectPath)}`,
        },
      });
      return { error };
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        role,
        isSupplier: role === "individual" || role === "agency",
        isTraveler: role === "traveler",
        isPartner: role === "partner",
        isAdmin: role === "admin",
        agencyId,
        loading,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        refreshSupplierStatus,
        refreshTravelerStatus,
        resendSignupOtp,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
